/**
 * [Phase 2c.6-A.2.1 D-2 / RL-35] SnakeStandaloneDraftBoard
 *
 * Centerpiece for snake-format leagues - opens in a new tab from the
 * "DRAFT BOARD ↗" button. Reuses snake's `SnakeDraftContext` WS (mounted
 * by SnakeDraftBoardPage).
 *
 * Visual contract:
 *   - LM tile grid header - 1 tile per manager with draft slot, headshot
 *     proxy (avatar dot), team_name, ON CLOCK / ON DECK pills
 *   - Green-glow on-clock pill - pulsing green box-shadow
 *   - Pastel-orange on-deck pill - softer non-pulsing glow
 *   - Pick grid - rows = rounds, cols = managers in snake order
 *     (snake reverses on even rounds - visually rendered straight; pick
 *     cells indicate the actual manager by column lookup)
 *   - FlipCard pick animation on PICK_MADE (front: shared PlayerCell, back:
 *     blank placeholder)
 *   - HF2-5 / PGF Draft Board Sweep Item 6 - uses the shared
 *     `TeamTile` / `PlayerCell` primitives (boards/) common to both formats
 *
 * No bid display, no auction-bid animation, no nomination modal - snake
 * is pick-only.
 */
import { useEffect, useState, useMemo, useRef } from "react";
import { AnimatePresence } from "framer-motion";
import axios from "axios";
import { Sun, Moon, Wifi, WifiOff } from "lucide-react";
import { useSnakeDraft } from "../../contexts/SnakeDraftContext";
import { authHeaders, useAuth } from "../../contexts/AuthContext";
import FlipCard from "../FlipCard";
import TeamTile from "../draft/boards/TeamTile";
import PlayerCell, { PICK_CELL_MIN_H } from "../draft/boards/PlayerCell";
import BoardStyleSelector from "../BoardStyleSelector";
import { useBoardStyle } from "../../hooks/useBoardStyle";
import { BOARD_STYLES } from "../../utils/boardStyles";
import TraditionalDraftBoard from "../draft/boards/TraditionalDraftBoard";
import WhiteboardDraftBoard from "../draft/boards/WhiteboardDraftBoard";
import SnakeDraftBoardTicker from "./SnakeDraftBoardTicker";
import DraftEndRecapModal from "../draft/DraftEndRecapModal";
import BetweenRoundsCountdownModal from "../draft/BetweenRoundsCountdownModal";
import FullscreenToggle from "../draft/FullscreenToggle";
import PlayerAnalysisModal from "../draft/PlayerAnalysisModal";
import PreDraftCountdownOverlay from "../draft/PreDraftCountdownOverlay";
import SnakeBoardTimer from "./SnakeBoardTimer";

const API = process.env.REACT_APP_BACKEND_URL;

// Snake-order column resolver: in a snake draft, odd rounds go 1→N, even
// rounds go N→1. The visual board shows ROUNDS as rows and the manager
// who picks at that slot as the column.
function pickAtRoundSlot(picks, round, slot, teamCount) {
  // Snake math: in round R (1-indexed), slot S (1-indexed) means the
  // manager at draft_order index (R odd ? S-1 : teamCount-S).
  return picks.find(
    (p) => p.round === round && p.pick_in_round === slot
  );
}

// [A.2.1 D-2] Snake-only flip-timing wrapper around the shared PlayerCell.
// Just-landed cells start unflipped (back face - blank), then animate to
// flipped (front face - player info) on the next paint. Older cells (already
// on the board at mount) render flipped from t=0 - no animation. Content
// rendering itself is delegated to the shared <PlayerCell> (Item 6).
// [Board Tile Polish item 1] Pick-cell height is shared with TeamTile so a
// team tile is exactly as tall as a player card. Single constant, one owner.

function PickCell({ pick, isDark, justLanded, onClick }) {
  const cellBg = isDark ? "#0F0F12" : "#FFFFFF";
  const cellBorder = isDark ? "#2A2A2A" : "#E5E5E5";

  const [flipped, setFlipped] = useState(!justLanded);
  useEffect(() => {
    if (justLanded && !flipped) {
      const r = requestAnimationFrame(() => setFlipped(true));
      return () => cancelAnimationFrame(r);
    }
  }, [justLanded, flipped]);

  const back = (
    <div
      className="rounded-md"
      style={{ minHeight: PICK_CELL_MIN_H, background: cellBg, border: `1px dashed ${cellBorder}` }}
    />
  );

  if (!pick) {
    return back;
  }

  return (
    <div style={{ minHeight: PICK_CELL_MIN_H }}>
      <FlipCard
        isFlipped={flipped}
        duration={1.0}
        perspective={1000}
        testId={`board-pick-${pick.overall_pick}`}
        back={back}
        front={
          <PlayerCell player={pick.player} isDark={isDark} showPaid={false} onClick={onClick} />
        }
      />
    </div>
  );
}



// [BOARD MIRROR item 4] One persistent slot per COLUMN. Keyed by column index
// by the parent (never by manager id), so the instance survives the draft
// order landing - which is what lets the reveal animate instead of remounting
// the whole tile row. Empty slot = blank tile (no order chosen yet); when its
// manager arrives it flips in, using the same FlipCard primitive as PickCell.
function TeamTileSlot({ manager, slot, onClock, onDeck, isDark }) {
  const cellBg = isDark ? "#0F0F12" : "#FFFFFF";
  const cellBorder = isDark ? "#2A2A2A" : "#E5E5E5";
  const [flipped, setFlipped] = useState(!!manager);

  useEffect(() => {
    if (manager && !flipped) {
      const r = requestAnimationFrame(() => setFlipped(true));
      return () => cancelAnimationFrame(r);
    }
  }, [manager, flipped]);

  const back = (
    <div
      className="rounded-md"
      style={{ minHeight: PICK_CELL_MIN_H, background: cellBg, border: `1px dashed ${cellBorder}` }}
    />
  );

  if (!manager) {
    return <div style={{ minHeight: PICK_CELL_MIN_H }} data-testid={`board-team-slot-${slot}-empty`}>{back}</div>;
  }

  return (
    <div style={{ minHeight: PICK_CELL_MIN_H }}>
      <FlipCard
        isFlipped={flipped}
        duration={1.0}
        perspective={1000}
        testId={`board-team-slot-${slot}`}
        back={back}
        front={
          <TeamTile
            manager={manager}
            slot={slot}
            onClock={onClock}
            onDeck={onDeck}
            isDark={isDark}
          />
        }
      />
    </div>
  );
}

export default function SnakeStandaloneDraftBoard() {
  const {
    draftState, isConnected, recapDismissed, dismissRecap,
    roundStartedEvent, consumeRoundStartedEvent, predraftProgress,
  } = useSnakeDraft();
  const { user } = useAuth();
  const [isDark, setIsDark] = useState(true);
  const [fullHistory, setFullHistory] = useState([]);
  const [recentlyLanded, setRecentlyLanded] = useState(null);
  // [RL-51] Active player for PlayerAnalysisModal - set on board-pick click.
  const [analysisPlayer, setAnalysisPlayer] = useState(null);
  const fetchedLeagueId = useRef(null);

  const leagueId = draftState?.league_id;
  const draftOrder = draftState?.draft_order || [];
  const managers = draftState?.managers || {};
  const rosterSize = draftState?.roster_size || 0;
  const teamCount = draftOrder.length;
  // [BOARD MIRROR items 2+3] The board opens from the LOBBY now, before an
  // order exists. Pre-draft it scaffolds from league setup (num_teams x
  // roster_size); when the commissioner starts, `draft_order` takes over and
  // the columns re-lay-out to whoever actually made it into the room.
  const columnCount = teamCount || draftState?.settings?.num_teams || 0;
  const isPredraft = !teamCount;

  // Which manager occupies each column, as the reveal progresses. Absolute
  // count off the pacer's broadcast, so a late frame cannot desync us and a
  // board opened mid-reveal catches up from its STATE_SNAPSHOT.
  const slotManagerIds = useMemo(() => {
    let revealed = draftOrder;
    if (!revealed.length && predraftProgress?.order?.length) {
      const order = predraftProgress.order;
      const n = predraftProgress.phase === "REVEAL"
        ? (predraftProgress.revealed_count || 0)
        : order.length;
      revealed = order.slice(0, n);
    }
    return Array.from({ length: columnCount }, (_, i) => revealed[i] || null);
  }, [draftOrder, predraftProgress, columnCount]);
  const currentPickerId = draftState?.current_picker_id;
  const currentRound = draftState?.current_round || 1;
  const currentPickInRound = draftState?.current_pick_in_round || 1;

  // [PGF Draft Board Sweep Item 3] Board-style selector - mirrors auction
  // StandaloneDraftBoard's usage. Snake has no `default_board_style` on
  // draftState (no backend field exists yet) - the hook self-hydrates the
  // user's personal preference from localStorage + the backend regardless,
  // so this degrades gracefully to the product default until a commissioner
  // league-default wire-up lands. isCommissioner is not yet derivable from
  // snake's draftState (no commissioner_id broadcast) - flagged, not
  // invented; personal per-user override still works fully.
  const {
    style: boardStyle,
    userOverride: boardUserOverride,
    setLocalStyle: setLocalBoardStyle,
  } = useBoardStyle(leagueId);
  const isCommissioner = false;

  // Shared cross-format helpers (Traditional / Whiteboard board styles) -
  // orderedManagers by draft slot, rounds as 1-indexed round numbers, and
  // a getCell(uid, roundIdx) adapter over the untrimmed pick history.
  // Padded to the column count with placeholders so the Traditional and
  // Whiteboard styles hold their grid pre-draft. Whiteboard keys its team
  // name by user_id, so the placeholder -> real id swap replays its
  // clip-path write-in: the reveal animation comes free.
  const orderedManagers = slotManagerIds.map((uid, i) =>
    (uid && managers[uid]) || { user_id: `__slot_${i}`, team_name: "" },
  );
  const rounds = Array.from({ length: rosterSize || 0 }, (_, i) => i + 1);
  const getCell = (uid, idx) =>
    fullHistory.find((p) => p.round === idx + 1 && p.team_id === uid) || null;

  // [PGF Draft Board Sweep Item 2] Auto-scroll the current round's row
  // into view during the 3s BetweenRoundsCountdownModal window. Snake's
  // roundStartedEvent is server-tracked (round >= 2 only) - reliable by
  // construction, no trimmed-array workaround needed here.
  const roundRowRefs = useRef({});
  useEffect(() => {
    if (!roundStartedEvent) return;
    const el = roundRowRefs.current[roundStartedEvent.round];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [roundStartedEvent]);

  // On-deck = next picker. In snake, that's slot+1 in odd rounds or
  // slot-1 in even rounds, with round wraparound.
  const onDeckId = useMemo(() => {
    if (!teamCount || !rosterSize) return null;
    let r = currentRound;
    let s = currentPickInRound + 1;
    if (s > teamCount) {
      r = currentRound + 1;
      s = teamCount;  // last slot of new round (because direction flips)
    }
    if (r > rosterSize) return null;
    const oddRound = r % 2 === 1;
    const idx = oddRound ? s - 1 : teamCount - s;
    return draftOrder[idx];
  }, [currentRound, currentPickInRound, teamCount, rosterSize, draftOrder]);

  // Fetch full pick history once on mount (engine's to_dict ships only
  // last 30 picks; for a full grid we need the lot).
  // A board opened in the lobby would otherwise burn its single fetch on an
  // empty draft, so the phase is part of the guard key.
  useEffect(() => {
    const fetchKey = `${leagueId}:${isPredraft ? "lobby" : "live"}`;
    if (!leagueId || fetchedLeagueId.current === fetchKey) return;
    fetchedLeagueId.current = fetchKey;
    axios
      .get(`${API}/api/draft/${leagueId}/pick-history`, { headers: authHeaders() })
      .then((r) => setFullHistory(r?.data?.pick_history || []))
      .catch(() => { /* will fall back to WS-streamed picks */ });
  }, [leagueId, isPredraft]);

  // Merge WS-streamed pick_history into fullHistory (idempotent on overall_pick).
  useEffect(() => {
    if (!draftState?.pick_history?.length) return;
    setFullHistory((prev) => {
      const byOverall = new Map(prev.map((p) => [p.overall_pick, p]));
      for (const p of draftState.pick_history) {
        byOverall.set(p.overall_pick, p);
      }
      return Array.from(byOverall.values()).sort(
        (a, b) => a.overall_pick - b.overall_pick
      );
    });
    // Mark the most recent pick as just-landed for FlipCard animation.
    const last = draftState.pick_history[draftState.pick_history.length - 1];
    if (last) {
      setRecentlyLanded(last.request_id || last.overall_pick);
      const t = setTimeout(() => setRecentlyLanded(null), 1800);
      return () => clearTimeout(t);
    }
  }, [draftState?.pick_history]);

  const bg = isDark ? "#0A0A0A" : "#F5F5F5";
  const fg = isDark ? "#F5F5F5" : "#0A0A0A";
  const subColor = isDark ? "#9AA0A6" : "#5F6368";
  const dividerColor = isDark ? "#2E2E2E" : "#D5D5D5";

  // Pre-draft the header narrates the phase instead of "Pick 1 of 0".
  const predraftStatus = predraftProgress?.phase === "COUNTDOWN"
    ? "Draft starting"
    : predraftProgress?.phase === "REVEAL"
    ? "Draft order reveal"
    : `${columnCount} teams · ${rosterSize} rounds · waiting for the commissioner to start`;

  return (
    <div
      className="h-screen flex flex-col relative overflow-hidden"
      style={{ background: bg, color: fg }}
      data-testid="snake-standalone-draft-board"
    >
      {/* [Snake Board Fixes item 1] Pinned chrome - title, clock and team
          tiles never scroll. Only the round rows scroll, inside the
          `board-scroll-region` pane below. Single code path, no
          viewport-conditional fallback (founder verdict): the board is a
          TV / second-monitor surface and the clock must always be on
          screen. */}
      <div className="shrink-0 px-4 sm:px-6 pt-4 sm:pt-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div
            className="w-2 h-7 rounded-full"
            style={{
              background: "#FF6B00",
              boxShadow: "0 0 10px rgba(255,107,0,0.6)",
            }}
          />
          <div>
            <div
              className="font-display font-black text-xl sm:text-2xl uppercase tracking-wider"
              style={{ color: fg }}
            >
              Draft Board · Snake
            </div>
            <div className="font-body text-xs" style={{ color: subColor }} data-testid="board-status-line">
              {isPredraft ? predraftStatus : (
                <>
                  Round {currentRound} · Pick {currentPickInRound} of {columnCount}
                  {" · "}
                  {currentPickerId && managers[currentPickerId]
                    ? `${managers[currentPickerId].team_name} on the clock`
                    : draftState?.state === "DRAFT_COMPLETE" ? "DRAFT COMPLETE" : "-"}
                </>
              )}
            </div>
          </div>
        </div>
        {/* [HF clock-on-board] Persistent on-the-clock timer - center of
            chrome bar so it's the focal point during DRAFTING. Hides
            during DRAFT_COMPLETE / between-rounds windows since neither
            carries a live pick clock. Color escalates red in last 5s. */}
        <SnakeBoardTimer draftState={draftState} isDark={isDark} />
        <div className="flex items-center gap-3">
          <span data-testid="ws-indicator">
            {isConnected ? (
              <Wifi size={16} style={{ color: "rgb(60,210,80)" }} />
            ) : (
              <WifiOff size={16} style={{ color: "#FF5555" }} />
            )}
          </span>
          {/* [A.2.6] Fullscreen toggle - projector / second-monitor setup */}
          <FullscreenToggle
            className="w-7 h-7 rounded flex items-center justify-center"
            color={fg}
            testId="fullscreen-toggle-snake"
          />
          {/* [PGF Draft Board Sweep Item 3] Board style selector - mirrors
              auction StandaloneDraftBoard's placement next to theme toggle. */}
          <BoardStyleSelector
            leagueId={leagueId}
            activeStyle={boardStyle}
            userOverride={boardUserOverride}
            isCommissioner={isCommissioner}
            onLocalStyleChange={setLocalBoardStyle}
          />
          <button
            type="button"
            onClick={() => setIsDark((v) => !v)}
            className="w-7 h-7 rounded flex items-center justify-center"
            data-testid="theme-toggle"
            aria-label="Toggle theme"
          >
            {isDark ? <Sun size={14} color={fg} /> : <Moon size={14} color={fg} />}
          </button>
        </div>
      </div>

      {/* [HF4.5b item 1 / HF-POST-CHRIS Gap 4.A] LM tile grid header - wrapped
          in the same flex-row + leading round-label spacer the pick grid uses,
          so the LM tiles align column-for-column with the player tiles below.
          The leading 40/48px shim mirrors the `R{round}` label cell width.
          [Snake Board Fixes item 1] Lives in the pinned chrome region now -
          no `sticky` needed because it is outside the scroll pane. */}
      {boardStyle === BOARD_STYLES.PGF_STANDARD && (
        <>
          <div className="flex items-stretch gap-2 mb-4">
            <div
              className="w-10 sm:w-12 shrink-0"
              aria-hidden="true"
              data-testid="board-mgr-grid-shim"
            />
            <div
              className="flex-1 grid gap-2"
              style={{
                gridTemplateColumns: `repeat(${columnCount || 1}, minmax(0, 1fr))`,
              }}
              data-testid="board-mgr-grid"
            >
              {slotManagerIds.map((mgrId, idx) => (
                <TeamTileSlot
                  key={`slot-${idx}`}
                  manager={mgrId ? managers[mgrId] : null}
                  slot={idx + 1}
                  onClock={!!mgrId && mgrId === currentPickerId}
                  onDeck={!!mgrId && mgrId === onDeckId}
                  isDark={isDark}
                />
              ))}
            </div>
          </div>

          <div
            className="h-px mb-4"
            style={{ background: dividerColor }}
          />
        </>
      )}
      </div>

      {/* [Snake Board Fixes item 1] The ONLY scrolling region. */}
      <div
        className="flex-1 min-h-0 overflow-auto px-4 sm:px-6 pb-4"
        data-testid="board-scroll-region"
      >
      {boardStyle === BOARD_STYLES.PGF_STANDARD && (
        <>
          {/* Pick grid - rows = rounds, cols = manager slots */}
          <div
            className="space-y-2"
            data-testid="board-pick-grid"
          >
            {rounds.map((round) => {
              return (
                <div
                  key={round}
                  className="flex items-stretch gap-2"
                >
                  <div
                    ref={(el) => { roundRowRefs.current[round] = el; }}
                    className="w-10 sm:w-12 flex items-center justify-center font-display font-black text-xs sm:text-sm shrink-0"
                    style={{ color: subColor }}
                    data-testid={`board-round-label-${round}`}
                  >
                    R{round}
                  </div>
                  <div
                    className="flex-1 grid gap-2"
                    style={{
                      gridTemplateColumns: `repeat(${columnCount || 1}, minmax(0, 1fr))`,
                    }}
                  >
                    {Array.from({ length: columnCount }, (_, ci) => {
                      // Column ci is draft_order[ci] (slot ci+1 visually).
                      // For odd rounds, slot ci+1 = pick_in_round ci+1.
                      // For even rounds, slot ci+1 picks at pick_in_round = teamCount-ci.
                      const slot = round % 2 === 1 ? ci + 1 : columnCount - ci;
                      const pick = pickAtRoundSlot(fullHistory, round, slot, columnCount);
                      const just = pick && (
                        recentlyLanded === pick.request_id ||
                        recentlyLanded === pick.overall_pick
                      );
                      return (
                        <PickCell
                          key={`${round}-${ci}`}
                          pick={pick}
                          isDark={isDark}
                          justLanded={just}
                          onClick={(p) => p?.id && setAnalysisPlayer(p)}
                        />
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* [PGF Draft Board Sweep Item 3] Traditional / Whiteboard board
          styles - snake reuses the same shared board components as auction,
          via the getCell(uid, roundIdx) + orderedManagers adapters above.
          showBid/showBudget are both false - snake has no purchase cost. */}
      {boardStyle === BOARD_STYLES.TRADITIONAL && (
        <TraditionalDraftBoard
          orderedManagers={orderedManagers}
          rounds={rounds}
          getCell={getCell}
          isDark={isDark}
          showBid={false}
          showBudget={false}
        />
      )}
      {boardStyle === BOARD_STYLES.WHITEBOARD && (
        <WhiteboardDraftBoard
          orderedManagers={orderedManagers}
          rounds={rounds}
          getCell={getCell}
          isDark={isDark}
          leagueId={leagueId}
          showBid={false}
          showBudget={false}
        />
      )}

      {/* Legal disclaimer footer */}
      <div
        className="mt-8 text-center font-body text-[10px] tracking-wider"
        style={{ color: subColor }}
      >
        PREGAME FANTASY · Live snake draft board · Real-time over WebSocket
      </div>
      </div>

      {/* Animation keyframe */}
      <style>{`@keyframes pgf-pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.6; }
      }`}</style>

      {/* [BOARD MIRROR item 5] The draft-start countdown, mirrored. Same
          overlay component the lobby renders - the board just follows the
          commissioner's absolute number off the broadcast and plays no
          audio. The board keeps rendering underneath: overlay on, overlay
          off, no remount of the grid. */}
      <AnimatePresence>
        {predraftProgress?.phase === "COUNTDOWN" && (
          <PreDraftCountdownOverlay
            display={predraftProgress.countdown}
            firstTeam={
              managers[(predraftProgress.order || [])[0]]?.team_name || ""
            }
            firstLabel="First pick"
          />
        )}
      </AnimatePresence>

      {/* [HF3 P0-2] DraftEndRecapModal also mounts here so the modal fires
          even when the user is watching the standalone board (the founder's
          observed failure mode). dismissRecap persists via localStorage so
          dismissing here keeps it dismissed in the room tab too. */}
      {draftState?.state === "DRAFT_COMPLETE" && !recapDismissed && leagueId && (
        <DraftEndRecapModal
          leagueId={leagueId}
          onDismiss={dismissRecap}
          surface="standalone-board"
        />
      )}

      {/* [RL-54] Round-1 cinematic countdown. Fires on every draft start
          (mock re-starts included - Q-A2.2-4). prewarm() on GO eliminates
          first-pick TTS latency. */}
      {/* [HF4.5] BetweenRoundsCountdownModal - fires for rounds 2..N only.
          Round 1 is covered by the lobby's 10s DraftCountdown; the snake
          ROUND_STARTED handler in SnakeDraftContext only emits an event for
          round >= 2 (round 1 event is dropped client-side). */}
      {roundStartedEvent && roundStartedEvent.round >= 2 && (
        <BetweenRoundsCountdownModal
          round={roundStartedEvent.round}
          onComplete={consumeRoundStartedEvent}
          onSkip={consumeRoundStartedEvent}
          isDark={isDark}
        />
      )}

      {/* [RL-51] PlayerAnalysisModal - mounts on player-card click.
          Lazy POST + 3-column layout (consensus / ADP-Proj / roster-fit). */}
      {analysisPlayer && leagueId && (
        <PlayerAnalysisModal
          leagueId={leagueId}
          player={analysisPlayer}
          onClose={() => setAnalysisPlayer(null)}
          isDark={isDark}
          draftEpoch={draftState?.total_picks_made || 0}
          showPgfValue={false}
        />
      )}

      {/* [PGF Draft Board Sweep Item 1] Snake ticker - last flex child of
          the fixed shell (Snake Board Fixes item 1), so it holds the
          bottom edge without `position: fixed`. Its own z-index keeps it
          above the between-rounds modal backdrop (item 6). */}
      <SnakeDraftBoardTicker isDark={isDark} />
    </div>
  );
}
